import { CheckCircle, Circle, Users } from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { SearchInput } from "@/components/shared/search-input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { type TrainingUser, useAdminTraining } from "@/hooks/use-admin-training";
import { useDebouncedValue } from "@/hooks/use-debounced-value";

function formatDate(dateStr: string | null) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function CompletionCell({
  completed,
  completedAt,
}: {
  completed: boolean;
  completedAt: string | null;
}) {
  if (completed) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
          <CheckCircle className="h-3 w-3" />
          Done
        </span>
        {completedAt && (
          <span className="text-[10px] text-muted-foreground">{formatDate(completedAt)}</span>
        )}
      </div>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/50">
      <Circle className="h-3 w-3" />—
    </span>
  );
}

function UserRow({ user, courseCount }: { user: TrainingUser; courseCount: number }) {
  const completedCount = user.courseProgress.filter((p) => p.completedAt !== null).length;
  const allDone = completedCount === courseCount && courseCount > 0;

  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
      <td className="px-4 py-3">
        <div className="font-medium text-foreground">{user.name}</div>
        <div className="text-xs text-muted-foreground">{user.email}</div>
      </td>
      <td className="px-4 py-3">
        <span
          className={`text-xs font-semibold ${allDone ? "text-green-700" : "text-muted-foreground"}`}
        >
          {completedCount} / {courseCount}
        </span>
      </td>
      {user.courseProgress.map((p) => (
        <td key={p.courseId} className="px-4 py-3">
          <CompletionCell completed={p.completedAt !== null} completedAt={p.completedAt} />
        </td>
      ))}
    </tr>
  );
}

const SKELETON_COL_KEYS = ["sk-c-0", "sk-c-1", "sk-c-2", "sk-c-3", "sk-c-4"];

function SkeletonRow({ cols }: { cols: number }) {
  const keys = SKELETON_COL_KEYS.slice(0, Math.min(cols, SKELETON_COL_KEYS.length));
  return (
    <tr className="border-b border-border">
      <td className="px-4 py-3">
        <Skeleton className="h-4 w-32 mb-1" />
        <Skeleton className="h-3 w-44" />
      </td>
      <td className="px-4 py-3">
        <Skeleton className="h-3 w-10" />
      </td>
      {keys.map((key) => (
        <td key={key} className="px-4 py-3">
          <Skeleton className="h-3 w-12" />
        </td>
      ))}
    </tr>
  );
}

export function TrainingTab() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const { data, isLoading, isError, error, refetch } = useAdminTraining(debouncedSearch);

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Failed to load training data"}
        onRetry={() => void refetch()}
      />
    );
  }

  const courses = data?.courses ?? [];
  const users = data?.data ?? [];

  return (
    <div className="space-y-6">
      <Card className="border-border/50">
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle
              style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, color: "#0f1944" }}
            >
              Training Progress
            </CardTitle>
            <CardDescription>See which courses each user has completed.</CardDescription>
          </div>
          <SearchInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search by name or email..."
            className="max-w-xs"
          />
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: 500 + courses.length * 160 }}>
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
                    Completed
                  </th>
                  {courses.map((c) => (
                    <th
                      key={c.id}
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap"
                      style={{ minWidth: 160 }}
                    >
                      {c.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <>
                    <SkeletonRow cols={courses.length} />
                    <SkeletonRow cols={courses.length} />
                    <SkeletonRow cols={courses.length} />
                  </>
                )}
                {!isLoading && users.length === 0 && (
                  <tr>
                    <td colSpan={2 + courses.length} className="px-4 py-12">
                      <EmptyState
                        icon={Users}
                        title="No users found"
                        description={search ? "Try a different search." : "No users yet."}
                      />
                    </td>
                  </tr>
                )}
                {users.map((user) => (
                  <UserRow key={user.id} user={user} courseCount={courses.length} />
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
